/**
 * `.TTM` — Dynamix animation bytecode, v3.38 (docs/formats/ttm-animation.md,
 * VERIFIED against the full 28-file Red Baron corpus: the 23 catalog files
 * in the seven volumes — 6 presentation scripts + 17 per-aircraft cockpit
 * scripts — plus 5 more loose, expansion-only aircraft TTMs that shadow
 * nothing in any volume. A `.TTM` is a **flat** chunk stream (no top-level
 * container):
 *
 * ```
 * VER:  5 bytes, RAW           NUL-terminated version string ("3.38\0")
 * PAG:  2 bytes, RAW           uint16le totalFrames
 * TT3:  compressed (LZW)       the bytecode — see ttm-bytecode.ts
 * TTI:  container
 *   TAG: RAW                   uint16 count; count * {uint16 index; cstring label}
 * ```
 *
 * Every real Red Baron `.TTM` disassembles its `TT3:` bytecode to the exact
 * byte with zero slack (ttm-bytecode.ts's module doc has the framing rule
 * that makes that possible — the point-list special case for the two
 * polygon-draw opcodes).
 */

import { parseChunks, requireChunk, requireLeaf } from "./chunk.ts";
import { decodeCompressedChunk } from "./compression.ts";
import { BinaryReader } from "./binary-reader.ts";
import { disassembleTtmBytecode, type TtmInstruction } from "./ttm-bytecode.ts";

export type { TtmArgs, TtmInstruction, TtmPoint } from "./ttm-bytecode.ts";
export { disassembleTtmBytecode, encodeTtmBytecode } from "./ttm-bytecode.ts";
export { DRAW_EMPTY_POLY_OP, DRAW_FILLED_POLY_OP, TTM_OPCODE_NAMES, ttmOpcodeName } from "./ttm-opcodes.ts";

/** One entry from the `TTI:`/`TAG:` label table. */
export interface TtmTagEntry {
  readonly index: number;
  readonly label: string;
}

export interface TtmScript {
  /** The `VER:` string, e.g. `"3.38"` — every shipped Red Baron `.TTM` has this exact value. */
  readonly version: string;
  /** `PAG:`'s declared total frame count. */
  readonly totalFrames: number;
  readonly instructions: readonly TtmInstruction[];
  readonly tags: readonly TtmTagEntry[];
}

/**
 * Parses a whole `.TTM` file's bytes: the container framing plus a full
 * disassembly of its `TT3:` bytecode. Throws on any structural violation —
 * a missing required chunk, an invalid arg-count nibble, or a truncated
 * bytecode stream — never silently produces a partial result.
 */
export function parseTtm(source: ArrayBufferLike | ArrayBufferView): TtmScript {
  const nodes = parseChunks(source);

  const verLeaf = requireLeaf(nodes, "VER:", ".TTM file");
  const version = new BinaryReader(verLeaf).cstring(verLeaf.length);

  const pagLeaf = requireLeaf(nodes, "PAG:", ".TTM file");
  const totalFrames = new BinaryReader(pagLeaf).u16();

  const tt3Leaf = requireLeaf(nodes, "TT3:", ".TTM file");
  const bytecode = decodeCompressedChunk(tt3Leaf);
  const instructions = disassembleTtmBytecode(bytecode);

  const ttiNode = requireChunk(nodes, "TTI:", ".TTM file");
  if (ttiNode.kind !== "container") {
    throw new Error(".TTM: 'TTI:' chunk must be a container");
  }
  const tagLeaf = requireLeaf(ttiNode.children, "TAG:", "TTI:");
  const tags = parseTagTable(tagLeaf);

  return { version, totalFrames, instructions, tags };
}

/** `uint16 count`, then `count * {uint16 index; NUL-terminated string label}` (docs/formats/ttm-animation.md). */
function parseTagTable(data: Uint8Array): TtmTagEntry[] {
  const reader = new BinaryReader(data);
  const count = reader.u16();
  const tags: TtmTagEntry[] = [];
  for (let i = 0; i < count; i++) {
    const index = reader.u16();
    const label = reader.cstring();
    tags.push({ index, label });
  }
  return tags;
}
